import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  X,
  ShieldCheck,
  LockKeyhole,
  Coffee,
  RefreshCw,
  CheckCircle2,
  Clock3,
  TrendingUp,
  TrendingDown,
  CalendarDays,
  TimerReset,
  Shield,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
} from "lucide-react";

import { Button } from "./ui/button";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function parseUserNumber(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;

  // Aceita 100000, 100000.50, 100000,50 e 100.000,50.
  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;

  return toNumber(normalized, 0);
}

function formatBrl(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "R$ 0,00";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatNumber(value, digits = 4) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "--";

  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
}

function formatDate(value) {
  if (!value) return "--";

  try {
    const raw = String(value);
    const normalized = raw.includes("T") ? raw : `${raw.replace(" ", "T")}-03:00`;
    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) return raw;

    return date.toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch (_) {
    return String(value);
  }
}


function lockDateMs(value) {
  if (!value) return null;

  try {
    const raw = String(value).trim();
    const normalized = raw.includes("T") ? raw : `${raw.replace(" ", "T")}-03:00`;
    const ms = new Date(normalized).getTime();
    return Number.isFinite(ms) ? ms : null;
  } catch (_) {
    return null;
  }
}

function lockProgress(contract) {
  const startMs = lockDateMs(contract?.started_at || contract?.activated_at);
  const endMs = lockDateMs(contract?.maturity_at);

  if (!startMs || !endMs || endMs <= startMs) {
    return {
      percent: 0,
      daysRemaining: contract?.live?.days_remaining ?? null,
      started: Boolean(startMs),
    };
  }

  const now = Date.now();
  const total = endMs - startMs;
  const elapsed = Math.max(0, Math.min(total, now - startMs));
  const percent = Math.max(0, Math.min(100, (elapsed / total) * 100));

  const liveDays = Number(contract?.live?.days_remaining);
  const daysRemaining = Number.isFinite(liveDays)
    ? Math.max(0, liveDays)
    : Math.max(0, Math.ceil((endMs - now) / 86400000));

  return {
    percent,
    daysRemaining,
    started: true,
  };
}

function contractProtectedPrice(contract) {
  return toNumber(
    contract?.protected_price_brl_per_bag ??
      contract?.initial_price_brl_per_bag,
    0
  );
}

function getLockBase() {
  if (typeof window === "undefined") return "https://cofy.app/api/lock";

  const host = String(window.location.hostname || "").toLowerCase();
  if (host === "cofy.app" || host === "www.cofy.app") {
    return `${window.location.origin}/api/lock`;
  }

  // Permite testar o front local usando o backend oficial.
  return "https://cofy.app/api/lock";
}

function statusInfo(contract) {
  const status = String(contract?.status || "").toUpperCase();
  const hedge = String(contract?.hedge_status || "").toUpperCase();

  if (status === "ACTIVE" && (hedge === "CONFIRMED" || contract?.protected === true)) {
    return {
      label: "PROTEGIDO",
      classes: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    };
  }

  if (status === "SIGNED" && hedge === "CONFIRMED") {
    return {
      label: "HEDGE CONFIRMADO",
      classes: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
    };
  }

  if (status === "SIGNED") {
    return {
      label: "AGUARDANDO HEDGE",
      classes: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    };
  }

  if (status === "DRAFT") {
    return {
      label: "RASCUNHO",
      classes: "border-slate-700 bg-slate-800 text-slate-300",
    };
  }

  if (status === "MATURED") {
    return {
      label: "VENCIDO",
      classes: "border-violet-500/30 bg-violet-500/10 text-violet-300",
    };
  }

  if (status === "SETTLED") {
    return {
      label: "LIQUIDADO",
      classes: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
    };
  }

  if (["CANCELED", "CANCELLED"].includes(status)) {
    return {
      label: "CANCELADO",
      classes: "border-red-500/30 bg-red-500/10 text-red-300",
    };
  }

  return {
    label: status || "PENDENTE",
    classes: "border-slate-700 bg-slate-800 text-slate-300",
  };
}

export default function CofyLockPanel({ open, onClose, accountMode = "real" }) {
  const [plans, setPlans] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [lastContractsUpdate, setLastContractsUpdate] = useState(null);
  const [expandedContractId, setExpandedContractId] = useState(null);

  const [coffeeType, setCoffeeType] = useState("CONILON");
  const [planId, setPlanId] = useState("");
  const [requestMode, setRequestMode] = useState("AMOUNT");
  const [amount, setAmount] = useState("");
  const [bags, setBags] = useState("");

  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [contracting, setContracting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [lockPolicy, setLockPolicy] = useState(null);
  const [loadingPolicy, setLoadingPolicy] = useState(false);

  const lockBase = useMemo(() => getLockBase(), []);

  const selectedPlan = useMemo(
    () => plans.find((item) => Number(item?.id) === Number(planId)),
    [plans, planId]
  );

  const fetchPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const response = await axios.get(`${lockBase}/plans.php`, { timeout: 8000 });
      const rows = Array.isArray(response?.data?.plans) ? response.data.plans : [];
      setPlans(rows);

      if (rows.length > 0) {
        setPlanId((current) => {
          if (rows.some((row) => Number(row.id) === Number(current))) return current;
          return String(rows[0].id);
        });
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Não foi possível carregar os planos do COFY LOCK."
      );
    } finally {
      setLoadingPlans(false);
    }
  }, [lockBase]);

  const fetchContracts = useCallback(async () => {
    setLoadingContracts(true);
    try {
      const response = await axios.get(`${lockBase}/list.php`, { timeout: 8000 });
      const rows = Array.isArray(response?.data?.contracts) ? response.data.contracts : [];
      setContracts(rows);
      setLastContractsUpdate(new Date());
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Não foi possível carregar seus contratos COFY LOCK."
      );
    } finally {
      setLoadingContracts(false);
    }
  }, [lockBase]);

  const fetchPolicy = useCallback(
    async (silent = false) => {
      setLoadingPolicy(true);

      try {
        const response = await axios.get(`${lockBase}/policy.php`, { timeout: 8000 });

        if (response?.data?.ok !== true || !response?.data?.policy) {
          throw new Error(
            response?.data?.error || "Não foi possível carregar seu limite do COFY LOCK."
          );
        }

        const policy = response.data.policy;
        setLockPolicy(policy);
        return policy;
      } catch (err) {
        if (!silent) {
          setError(
            err?.response?.data?.message ||
              err?.response?.data?.error ||
              err?.message ||
              "Não foi possível carregar seu limite do COFY LOCK."
          );
        }
        return null;
      } finally {
        setLoadingPolicy(false);
      }
    },
    [lockBase]
  );

  useEffect(() => {
    if (!open) return;
    setError("");
    setSuccess("");
    fetchPlans();
    fetchContracts();
    fetchPolicy(true);
  }, [open, fetchPlans, fetchContracts, fetchPolicy]);

  useEffect(() => {
    setQuote(null);
    setAccepted(false);
    setError("");
    setSuccess("");
  }, [coffeeType, planId, requestMode, amount, bags]);

  // Mantém preço, proteção, dias restantes e vencimento sincronizados
  // enquanto o painel estiver aberto. Se o Admin alterar a data,
  // o cliente passa a enxergar a mudança automaticamente.
  useEffect(() => {
    if (!open || accountMode !== "real") return undefined;

    const id = setInterval(() => {
      fetchContracts();
      fetchPolicy(true);
    }, 10000);

    return () => clearInterval(id);
  }, [open, accountMode, fetchContracts, fetchPolicy]);

  const makeRequestPayload = () => {
    const payload = {
      plan_id: Number(planId),
      coffee_type: coffeeType,
    };

    if (requestMode === "AMOUNT") {
      const value = parseUserNumber(amount);
      if (value <= 0) throw new Error("Informe o valor que deseja proteger.");
      payload.amount_brl = value;
    } else {
      const value = parseUserNumber(bags);
      if (value <= 0) throw new Error("Informe a quantidade de sacas.");
      payload.quantity_bags = value;
    }

    return payload;
  };

  const simulate = async () => {
    if (accountMode !== "real") {
      setError("O COFY LOCK está disponível somente na conta real.");
      return;
    }

    if (!planId) {
      setError("Selecione um plano.");
      return;
    }

    setQuoting(true);
    setError("");
    setSuccess("");
    setAccepted(false);

    try {
      const payload = makeRequestPayload();
      const response = await axios.post(`${lockBase}/quote.php`, payload, { timeout: 10000 });

      if (response?.data?.ok !== true) {
        throw new Error(response?.data?.error || "Não foi possível fazer a simulação.");
      }

      setQuote(response.data.data);
      await fetchPolicy(true);
    } catch (err) {
      setQuote(null);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Não foi possível simular o COFY LOCK."
      );
    } finally {
      setQuoting(false);
    }
  };

  const contractLock = async () => {
    if (!quote || contracting) return;

    if (!accepted) {
      setError("Você precisa aceitar as condições antes de contratar.");
      return;
    }

    if (accountMode !== "real") {
      setError("O COFY LOCK está disponível somente na conta real.");
      return;
    }

    setContracting(true);
    setError("");
    setSuccess("");

    try {
      const payload = makeRequestPayload();

      // Atualiza limite e saldo imediatamente antes da contratação.
      // O backend repetirá todas as validações; isto é apenas uma proteção de UX.
      const latestPolicy = (await fetchPolicy(true)) || lockPolicy;
      const requestedAmount = toNumber(quote?.quote?.protected_amount, 0);

      if (latestPolicy && requestedAmount > 0) {
        const availableLimit = toNumber(latestPolicy?.available_limit, 0);
        const walletBalance = toNumber(latestPolicy?.wallet_balance, 0);

        if (requestedAmount > availableLimit + 0.009) {
          if (latestPolicy?.profile_completed === false && latestPolicy?.custom_limit == null) {
            throw new Error(
              `Seu limite atual disponível é ${formatBrl(availableLimit)}. Complete seu cadastro para aumentar seu limite do COFY LOCK.`
            );
          }

          throw new Error(
            `O valor solicitado ultrapassa seu limite disponível de ${formatBrl(availableLimit)}. Para solicitar um limite maior, entre em contato com a COFY.`
          );
        }

        if (requestedAmount > walletBalance + 0.009) {
          throw new Error(
            `Saldo insuficiente para contratar. Valor necessário: ${formatBrl(
              requestedAmount
            )}. Saldo disponível: ${formatBrl(walletBalance)}.`
          );
        }
      }

      const createResponse = await axios.post(`${lockBase}/create.php`, payload, {
        timeout: 12000,
      });

      if (createResponse?.data?.ok !== true) {
        throw new Error(createResponse?.data?.error || "Não foi possível criar o COFY LOCK.");
      }

      const contractId = Number(createResponse?.data?.contract?.id);
      if (!contractId) throw new Error("O contrato foi criado sem identificação.");

      const signResponse = await axios.post(
        `${lockBase}/sign.php`,
        { contract_id: contractId, accepted: true },
        { timeout: 12000 }
      );

      if (signResponse?.data?.ok !== true) {
        throw new Error(signResponse?.data?.error || "Não foi possível assinar o COFY LOCK.");
      }

      const signedPolicy = signResponse?.data?.lock_policy || null;
      const reservedAmount = toNumber(signResponse?.data?.reservation?.amount, 0);

      if (signedPolicy) {
        setLockPolicy(signedPolicy);
      } else {
        await fetchPolicy(true);
      }

      setSuccess(
        reservedAmount > 0
          ? `COFY LOCK contratado e assinado. ${formatBrl(
              reservedAmount
            )} foram reservados na sua conta. A operação agora aguarda a confirmação do hedge pela COFY.`
          : "COFY LOCK contratado e assinado. O capital foi reservado e a operação agora aguarda a confirmação do hedge pela COFY."
      );
      setQuote(null);
      setAccepted(false);
      setAmount("");
      setBags("");
      await fetchContracts();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Não foi possível concluir a contratação."
      );
    } finally {
      setContracting(false);
    }
  };

  const quoteData = quote?.quote || {};
  const market = quote?.market || {};

  const quotedProtectedAmount = toNumber(quoteData?.protected_amount, 0);
  const policyLoaded = Boolean(
    lockPolicy && Number.isFinite(Number(lockPolicy?.effective_limit))
  );
  const effectiveLimit = toNumber(lockPolicy?.effective_limit, 0);
  const availableLimit = toNumber(lockPolicy?.available_limit, 0);
  const committedAmount = toNumber(lockPolicy?.committed_amount, 0);
  const walletBalance = toNumber(lockPolicy?.wallet_balance, 0);
  const customLimit =
    lockPolicy?.custom_limit == null ? null : toNumber(lockPolicy?.custom_limit, 0);
  const profileCompleted = lockPolicy?.profile_completed === true;

  const exceedsLockLimit =
    policyLoaded && quotedProtectedAmount > availableLimit + 0.009;

  const insufficientWalletBalance =
    policyLoaded && quotedProtectedAmount > walletBalance + 0.009;

  const quoteCanBeContracted =
    !policyLoaded || (!exceedsLockLimit && !insufficientWalletBalance);

  const sortedContracts = useMemo(() => {
    const rank = {
      ACTIVE: 0,
      SIGNED: 1,
      DRAFT: 2,
      MATURED: 3,
      SETTLED: 4,
      CANCELED: 5,
      CANCELLED: 5,
    };

    return [...contracts].sort((a, b) => {
      const sa = String(a?.status || "").toUpperCase();
      const sb = String(b?.status || "").toUpperCase();

      const ra = rank[sa] ?? 9;
      const rb = rank[sb] ?? 9;

      if (ra !== rb) return ra - rb;

      return (
        (lockDateMs(b?.created_at) || 0) -
        (lockDateMs(a?.created_at) || 0)
      );
    });
  }, [contracts]);

  const activeContracts = sortedContracts.filter(
    (contract) => String(contract?.status || "").toUpperCase() === "ACTIVE"
  );

  const totalProtectedActive = activeContracts.reduce(
    (sum, contract) => sum + toNumber(contract?.protected_amount, 0),
    0
  );

  // Todos os Hooks precisam ser executados antes de qualquer return condicional.
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[170] flex items-end justify-center bg-black/85 backdrop-blur-md sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[96vh] w-full overflow-y-auto rounded-t-3xl border border-slate-800 bg-slate-950 shadow-2xl sm:max-w-2xl sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-800 bg-slate-950/95 px-4 py-4 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-400/10 text-cyan-300">
              <LockKeyhole className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.18em] text-cyan-400">
                Proteção de preço
              </p>
              <h2 className="text-xl font-black text-white">COFY LOCK</h2>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-800 bg-slate-900 text-slate-300 transition hover:bg-slate-800"
            aria-label="Fechar COFY LOCK"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-4">
          {accountMode !== "real" && (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3">
              <p className="text-xs font-bold text-amber-300">COFY LOCK indisponível na conta Demo</p>
              <p className="mt-1 text-[10px] text-slate-400">
                Troque para a conta Real para contratar proteção.
              </p>
            </div>
          )}

          <div className="rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/10 via-slate-900 to-slate-900 p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-cyan-300" />
              <div>
                <h3 className="text-sm font-black text-white">Proteja o preço do seu café</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-400">
                  Fixe um preço de proteção durante o prazo contratado. Se o mercado cair abaixo do
                  preço protegido, o LOCK acompanha a diferença. Se o mercado subir, você continua
                  participando da alta.
                </p>
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Café</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setCoffeeType("ARABICA")}
                className={`rounded-2xl border p-3 text-left transition ${
                  coffeeType === "ARABICA"
                    ? "border-cyan-400/50 bg-cyan-400/10"
                    : "border-slate-800 bg-slate-900"
                }`}
              >
                <Coffee className={`h-5 w-5 ${coffeeType === "ARABICA" ? "text-cyan-300" : "text-slate-500"}`} />
                <p className="mt-2 text-sm font-black text-white">Arábica</p>
                <p className="mt-0.5 text-[9px] text-slate-500">ICE Nova York</p>
              </button>

              <button
                type="button"
                onClick={() => setCoffeeType("CONILON")}
                className={`rounded-2xl border p-3 text-left transition ${
                  coffeeType === "CONILON"
                    ? "border-cyan-400/50 bg-cyan-400/10"
                    : "border-slate-800 bg-slate-900"
                }`}
              >
                <Coffee className={`h-5 w-5 ${coffeeType === "CONILON" ? "text-cyan-300" : "text-slate-500"}`} />
                <p className="mt-2 text-sm font-black text-white">Conilon</p>
                <p className="mt-0.5 text-[9px] text-slate-500">ICE Londres</p>
              </button>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Prazo</p>
              {loadingPlans && <RefreshCw className="h-3.5 w-3.5 animate-spin text-slate-500" />}
            </div>

            {plans.length > 0 ? (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {plans
                  .filter((plan) =>
                    coffeeType === "ARABICA" ? plan.allow_arabica : plan.allow_conilon
                  )
                  .map((plan) => {
                    const selected = Number(planId) === Number(plan.id);
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => setPlanId(String(plan.id))}
                        className={`rounded-xl border px-3 py-3 text-left transition ${
                          selected
                            ? "border-cyan-400/50 bg-cyan-400/10"
                            : "border-slate-800 bg-slate-900"
                        }`}
                      >
                        <p className="text-xs font-black text-white">{plan.name}</p>
                        <p className="mt-1 text-[9px] text-slate-500">{plan.duration_days} dias</p>
                        <p className="mt-2 text-[10px] font-bold text-cyan-300">
                          Taxa {formatNumber(plan.fee_pct, 2)}%
                        </p>
                      </button>
                    );
                  })}
              </div>
            ) : (
              <div className="rounded-xl border border-slate-800 bg-slate-900 p-3 text-xs text-slate-500">
                Nenhum plano disponível.
              </div>
            )}
          </div>

          <div>
            <p className="mb-2 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">
              Quero proteger por
            </p>
            <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-900 p-1.5">
              <button
                type="button"
                onClick={() => setRequestMode("AMOUNT")}
                className={`rounded-xl px-3 py-2.5 text-xs font-black transition ${
                  requestMode === "AMOUNT" ? "bg-cyan-400 text-slate-950" : "text-slate-400"
                }`}
              >
                VALOR EM R$
              </button>
              <button
                type="button"
                onClick={() => setRequestMode("BAGS")}
                className={`rounded-xl px-3 py-2.5 text-xs font-black transition ${
                  requestMode === "BAGS" ? "bg-cyan-400 text-slate-950" : "text-slate-400"
                }`}
              >
                SACAS
              </button>
            </div>

            <div className="mt-2 rounded-2xl border border-slate-800 bg-slate-900 p-4">
              {requestMode === "AMOUNT" ? (
                <>
                  <label className="text-[10px] font-semibold text-slate-500">Valor protegido</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9,.]/g, ""))}
                    placeholder="100000"
                    className="mt-1 w-full bg-transparent text-2xl font-black text-white outline-none placeholder:text-slate-700"
                  />
                  <p className="mt-1 text-[9px] text-slate-600">Exemplo: 100000 para proteger R$ 100 mil.</p>
                </>
              ) : (
                <>
                  <label className="text-[10px] font-semibold text-slate-500">Quantidade de sacas</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={bags}
                    onChange={(e) => setBags(e.target.value.replace(/[^0-9,.]/g, ""))}
                    placeholder="100"
                    className="mt-1 w-full bg-transparent text-2xl font-black text-white outline-none placeholder:text-slate-700"
                  />
                  <p className="mt-1 text-[9px] text-slate-600">Informe a quantidade de sacas de 60 kg.</p>
                </>
              )}
            </div>
          </div>

          <Button
            type="button"
            onClick={simulate}
            disabled={quoting || accountMode !== "real" || !planId}
            className="h-12 w-full rounded-2xl bg-cyan-400 font-black text-slate-950 hover:bg-cyan-300 disabled:opacity-40"
          >
            {quoting ? (
              <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />SIMULANDO...</>
            ) : (
              <><ShieldCheck className="mr-2 h-4 w-4" />SIMULAR PROTEÇÃO</>
            )}
          </Button>

          {quote && (
            <div className="overflow-hidden rounded-2xl border border-cyan-500/30 bg-slate-900">
              <div className="border-b border-slate-800 bg-cyan-500/5 p-4">
                <p className="text-[9px] font-black uppercase tracking-[0.15em] text-cyan-300">
                  Simulação COFY LOCK
                </p>
                <div className="mt-1 flex items-baseline justify-between gap-3">
                  <h3 className="text-lg font-black text-white">{market.coffee_label}</h3>
                  <span className="text-xs font-bold text-slate-400">{market.contract}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-px bg-slate-800">
                <div className="bg-slate-950 p-3">
                  <p className="text-[8px] uppercase text-slate-600">Preço protegido</p>
                  <strong className="mt-1 block text-sm text-white">
                    {formatBrl(quoteData.reference_price_brl_per_bag)}
                  </strong>
                </div>
                <div className="bg-slate-950 p-3">
                  <p className="text-[8px] uppercase text-slate-600">Quantidade</p>
                  <strong className="mt-1 block text-sm text-white">
                    {formatNumber(quoteData.quantity_bags, 4)} sacas
                  </strong>
                </div>
                <div className="bg-slate-950 p-3">
                  <p className="text-[8px] uppercase text-slate-600">Valor protegido</p>
                  <strong className="mt-1 block text-sm text-cyan-300">
                    {formatBrl(quoteData.protected_amount)}
                  </strong>
                </div>
                <div className="bg-slate-950 p-3">
                  <p className="text-[8px] uppercase text-slate-600">Custo da proteção</p>
                  <strong className="mt-1 block text-sm text-white">{formatBrl(quoteData.fee_amount)}</strong>
                  <p className="text-[8px] text-slate-600">{formatNumber(quoteData.fee_pct, 2)}%</p>
                </div>
              </div>

              <div className="space-y-2 p-4">
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-slate-500">Prazo</span>
                  <strong className="text-white">
                    {selectedPlan?.duration_days || quote?.plan?.duration_days} dias
                  </strong>
                </div>
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-slate-500">Vencimento estimado</span>
                  <strong className="text-white">{formatDate(quoteData.maturity_at)}</strong>
                </div>
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-slate-500">Participação na alta</span>
                  <strong className="text-emerald-300">100%</strong>
                </div>

                {loadingPolicy && !policyLoaded && (
                  <div className="mt-3 flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 p-3 text-[10px] text-slate-400">
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    Verificando seu limite e saldo disponível...
                  </div>
                )}

                {policyLoaded && (
                  <div
                    className={`mt-3 rounded-2xl border p-3 ${
                      exceedsLockLimit || insufficientWalletBalance
                        ? "border-amber-500/30 bg-amber-500/10"
                        : "border-cyan-500/20 bg-cyan-500/5"
                    }`}
                  >
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-xl bg-slate-950/70 p-2.5">
                        <p className="text-[8px] uppercase text-slate-600">Limite total</p>
                        <strong className="mt-1 block text-xs text-white">
                          {formatBrl(effectiveLimit)}
                        </strong>
                      </div>

                      <div className="rounded-xl bg-slate-950/70 p-2.5">
                        <p className="text-[8px] uppercase text-slate-600">Limite disponível</p>
                        <strong
                          className={`mt-1 block text-xs ${
                            exceedsLockLimit ? "text-amber-300" : "text-cyan-300"
                          }`}
                        >
                          {formatBrl(availableLimit)}
                        </strong>
                      </div>

                      <div className="rounded-xl bg-slate-950/70 p-2.5">
                        <p className="text-[8px] uppercase text-slate-600">Saldo disponível</p>
                        <strong
                          className={`mt-1 block text-xs ${
                            insufficientWalletBalance ? "text-red-300" : "text-emerald-300"
                          }`}
                        >
                          {formatBrl(walletBalance)}
                        </strong>
                      </div>

                      <div className="rounded-xl bg-slate-950/70 p-2.5">
                        <p className="text-[8px] uppercase text-slate-600">Já comprometido</p>
                        <strong className="mt-1 block text-xs text-slate-300">
                          {formatBrl(committedAmount)}
                        </strong>
                      </div>
                    </div>

                    {!profileCompleted && customLimit == null && (
                      <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                        <p className="text-[10px] font-black text-amber-300">
                          Aumente seu limite para R$ 50 mil
                        </p>
                        <p className="mt-1 text-[9px] leading-relaxed text-slate-400">
                          Seu cadastro ainda não está completo. Enquanto isso, seu limite padrão do
                          COFY LOCK é de {formatBrl(effectiveLimit)}. Complete seu cadastro para ter
                          limite padrão de até R$ 50.000,00.
                        </p>
                      </div>
                    )}

                    {profileCompleted && customLimit == null && (
                      <p className="mt-3 text-[9px] leading-relaxed text-slate-400">
                        Seu cadastro está completo e seu limite padrão é de {formatBrl(effectiveLimit)}.
                        Se precisar de um limite maior, entre em contato com a COFY para solicitar análise.
                      </p>
                    )}

                    {customLimit != null && (
                      <p className="mt-3 text-[9px] leading-relaxed text-slate-400">
                        Você possui limite personalizado de {formatBrl(customLimit)} para o COFY LOCK.
                        Se precisar de outro valor, entre em contato com a COFY.
                      </p>
                    )}

                    {exceedsLockLimit && (
                      <div className="mt-3 rounded-xl border border-red-500/25 bg-red-500/10 p-3">
                        <p className="text-[10px] font-black text-red-300">
                          Esta simulação ultrapassa seu limite disponível
                        </p>
                        <p className="mt-1 text-[9px] leading-relaxed text-slate-400">
                          Você pode simular qualquer valor, mas só poderá contratar até {formatBrl(
                            availableLimit
                          )} neste momento.
                        </p>
                      </div>
                    )}

                    {insufficientWalletBalance && (
                      <div className="mt-3 rounded-xl border border-red-500/25 bg-red-500/10 p-3">
                        <p className="text-[10px] font-black text-red-300">Saldo insuficiente</p>
                        <p className="mt-1 text-[9px] leading-relaxed text-slate-400">
                          Para contratar esta proteção, você precisa ter {formatBrl(
                            quotedProtectedAmount
                          )} disponível na conta. Seu saldo atual é {formatBrl(walletBalance)}.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl border border-slate-800 bg-slate-950 p-3">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    disabled={!quoteCanBeContracted || loadingPolicy}
                    className="mt-1 h-4 w-4 accent-cyan-400 disabled:opacity-40"
                  />
                  <span className="text-[10px] leading-relaxed text-slate-400">
                    Li e concordo com as condições do COFY LOCK, incluindo preço de referência,
                    prazo, custo da proteção, reserva do capital na assinatura e necessidade de
                    confirmação do hedge antes da ativação.
                  </span>
                </label>

                <Button
                  type="button"
                  onClick={contractLock}
                  disabled={
                    !accepted ||
                    contracting ||
                    accountMode !== "real" ||
                    loadingPolicy ||
                    !quoteCanBeContracted
                  }
                  className="mt-2 h-14 w-full rounded-2xl bg-emerald-500 font-black text-slate-950 hover:bg-emerald-400 disabled:opacity-40"
                >
                  {contracting ? (
                    <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />CONTRATANDO...</>
                  ) : exceedsLockLimit ? (
                    <><Shield className="mr-2 h-4 w-4" />LIMITE INSUFICIENTE</>
                  ) : insufficientWalletBalance ? (
                    <><CircleDollarSign className="mr-2 h-4 w-4" />SALDO INSUFICIENTE</>
                  ) : (
                    <><LockKeyhole className="mr-2 h-4 w-4" />CONTRATAR PROTEÇÃO</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-red-500/25 bg-red-500/10 p-3 text-xs text-red-300">
              {error}
            </div>
          )}

          {success && (
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-3">
              <div className="flex gap-2">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-300" />
                <p className="text-xs leading-relaxed text-emerald-200">{success}</p>
              </div>
            </div>
          )}

          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">
                  Meus LOCKs
                </p>
                <p className="mt-0.5 text-xs text-slate-400">
                  Proteções contratadas e acompanhamento em tempo real
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {lastContractsUpdate && (
                  <span className="hidden text-[8px] text-slate-600 sm:inline">
                    Atualizado {lastContractsUpdate.toLocaleTimeString("pt-BR", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </span>
                )}

                <button
                  type="button"
                  onClick={fetchContracts}
                  disabled={loadingContracts}
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-800 bg-slate-900 text-slate-400 transition hover:bg-slate-800"
                  aria-label="Atualizar COFY LOCKs"
                >
                  <RefreshCw className={`h-4 w-4 ${loadingContracts ? "animate-spin" : ""}`} />
                </button>
              </div>
            </div>

            {activeContracts.length > 0 && (
              <div className="mb-3 grid grid-cols-2 gap-2">
                <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-3">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-emerald-300" />
                    <span className="text-[8px] font-black uppercase tracking-wider text-slate-500">
                      LOCKs ativos
                    </span>
                  </div>
                  <div className="mt-2 text-xl font-black text-emerald-300">
                    {activeContracts.length}
                  </div>
                </div>

                <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <div className="flex items-center gap-2">
                    <CircleDollarSign className="h-4 w-4 text-cyan-300" />
                    <span className="text-[8px] font-black uppercase tracking-wider text-slate-500">
                      Protegido ativo
                    </span>
                  </div>
                  <div className="mt-2 text-sm font-black text-cyan-300">
                    {formatBrl(totalProtectedActive)}
                  </div>
                </div>
              </div>
            )}

            {sortedContracts.length === 0 ? (
              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 text-center">
                <LockKeyhole className="mx-auto h-6 w-6 text-slate-600" />
                <p className="mt-2 text-xs font-bold text-slate-400">
                  Nenhum COFY LOCK contratado
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedContracts.map((contract) => {
                  const rawStatus = String(contract?.status || "").toUpperCase();
                  const status = statusInfo(contract);
                  const live = contract?.live || {};

                  const protectedPrice = contractProtectedPrice(contract);
                  const currentPrice =
                    live?.current_price_brl_per_bag != null
                      ? toNumber(live.current_price_brl_per_bag, null)
                      : null;

                  const variation = toNumber(live?.variation_pct, 0);
                  const protection = toNumber(live?.protection_amount, 0);
                  const upside = toNumber(live?.upside_amount, 0);
                  const differencePerBag =
                    currentPrice != null && protectedPrice > 0
                      ? currentPrice - protectedPrice
                      : 0;

                  const active = rawStatus === "ACTIVE";
                  const matured = rawStatus === "MATURED";
                  const settled = rawStatus === "SETTLED";
                  const cancelled = ["CANCELED", "CANCELLED"].includes(rawStatus);
                  const isExpanded = Number(expandedContractId) === Number(contract.id);

                  const progress = lockProgress(contract);
                  const daysRemaining = progress.daysRemaining;

                  const showLive =
                    active || matured || settled || contract?.protected === true;

                  return (
                    <div
                      key={contract.id}
                      className={`overflow-hidden rounded-2xl border bg-slate-900 ${
                        active
                          ? "border-emerald-500/25"
                          : matured
                          ? "border-violet-500/25"
                          : cancelled
                          ? "border-red-500/20"
                          : "border-slate-800"
                      }`}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-black text-white">
                                {contract.coffee_label ||
                                  (String(contract.coffee_type).toUpperCase() === "CONILON"
                                    ? "Café Conilon"
                                    : "Café Arábica")}
                              </p>
                              {contract?.benchmark_symbol && (
                                <span className="rounded-md bg-slate-950 px-1.5 py-0.5 text-[8px] font-bold text-slate-500">
                                  {contract.benchmark_symbol}
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 truncate text-[9px] text-slate-500">
                              {contract.contract_number}
                            </p>
                          </div>

                          <span
                            className={`shrink-0 rounded-full border px-2.5 py-1 text-[8px] font-black ${status.classes}`}
                          >
                            {status.label}
                          </span>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <div className="rounded-xl bg-slate-950 p-2.5">
                            <p className="text-[8px] uppercase text-slate-600">Preço protegido</p>
                            <strong className="mt-1 block text-xs text-white">
                              {protectedPrice > 0 ? formatBrl(protectedPrice) : "--"}
                            </strong>
                            <p className="mt-0.5 text-[8px] text-slate-700">por saca de 60 kg</p>
                          </div>

                          <div className="rounded-xl bg-slate-950 p-2.5">
                            <p className="text-[8px] uppercase text-slate-600">Preço atual</p>
                            <strong
                              className={`mt-1 block text-xs ${
                                currentPrice == null
                                  ? "text-slate-500"
                                  : currentPrice >= protectedPrice
                                  ? "text-emerald-300"
                                  : "text-cyan-300"
                              }`}
                            >
                              {currentPrice != null ? formatBrl(currentPrice) : "--"}
                            </strong>
                            <p className="mt-0.5 text-[8px] text-slate-700">referência COFY</p>
                          </div>

                          <div className="rounded-xl bg-slate-950 p-2.5">
                            <p className="text-[8px] uppercase text-slate-600">Valor protegido</p>
                            <strong className="mt-1 block text-xs text-cyan-300">
                              {formatBrl(contract.protected_amount)}
                            </strong>
                            <p className="mt-0.5 text-[8px] text-slate-700">
                              {formatNumber(contract.quantity_bags, 2)} sacas
                            </p>
                          </div>

                          <div className="rounded-xl bg-slate-950 p-2.5">
                            <p className="text-[8px] uppercase text-slate-600">Variação da referência</p>
                            {showLive && currentPrice != null ? (
                              <div
                                className={`mt-1 flex items-center gap-1 text-xs font-black ${
                                  variation >= 0 ? "text-emerald-300" : "text-red-300"
                                }`}
                              >
                                {variation >= 0 ? (
                                  <TrendingUp className="h-3 w-3" />
                                ) : (
                                  <TrendingDown className="h-3 w-3" />
                                )}
                                {variation >= 0 ? "+" : ""}
                                {formatNumber(variation, 2)}%
                              </div>
                            ) : (
                              <strong className="mt-1 block text-xs text-slate-500">--</strong>
                            )}
                            <p className="mt-0.5 text-[8px] text-slate-700">
                              {currentPrice != null
                                ? `${differencePerBag >= 0 ? "+" : "-"}${formatBrl(
                                    Math.abs(differencePerBag)
                                  )}/saca`
                                : "aguardando ativação"}
                            </p>
                          </div>
                        </div>

                        {active && showLive && currentPrice != null && (
                          <div
                            className={`mt-3 rounded-2xl border p-3 ${
                              protection > 0
                                ? "border-cyan-500/25 bg-cyan-500/5"
                                : "border-emerald-500/25 bg-emerald-500/5"
                            }`}
                          >
                            {protection > 0 ? (
                              <div className="flex items-start gap-3">
                                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-cyan-300" />
                                <div className="min-w-0">
                                  <p className="text-[8px] font-black uppercase tracking-wider text-cyan-300/80">
                                    PERDA EVITADA PELO LOCK
                                  </p>
                                  <p className="mt-1 text-xl font-black text-cyan-300">
                                    +{formatBrl(protection)}
                                  </p>
                                  <p className="mt-1 text-[9px] leading-relaxed text-slate-400">
                                    O mercado está abaixo do preço protegido. Este é o valor da desvalorização
                                    que a proteção do LOCK está evitando neste momento.
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-3">
                                <TrendingUp className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
                                <div className="min-w-0">
                                  <p className="text-[8px] font-black uppercase tracking-wider text-emerald-300/80">
                                    LUCRO ATUAL
                                  </p>
                                  <p className="mt-1 text-xl font-black text-emerald-300">
                                    +{formatBrl(upside)}
                                  </p>
                                  <p className="mt-1 text-[9px] leading-relaxed text-slate-400">
                                    O mercado está acima do preço protegido. Você continua participando
                                    integralmente da alta do café.
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {matured && (
                          <div className="mt-3 rounded-xl border border-violet-500/25 bg-violet-500/5 p-3">
                            <div className="flex items-start gap-2">
                              <TimerReset className="mt-0.5 h-4 w-4 shrink-0 text-violet-300" />
                              <div>
                                <p className="text-[9px] font-black text-violet-300">
                                  Prazo encerrado
                                </p>
                                <p className="mt-1 text-[9px] leading-relaxed text-slate-400">
                                  Este LOCK atingiu o vencimento e está aguardando o processo de
                                  liquidação conforme as regras do contrato.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {active && contract?.maturity_at && (
                          <div className="mt-3">
                            <div className="mb-1.5 flex items-center justify-between gap-2">
                              <span className="flex items-center gap-1 text-[9px] font-bold text-slate-500">
                                <Clock3 className="h-3 w-3" />
                                {daysRemaining != null
                                  ? `${daysRemaining} dia${daysRemaining === 1 ? "" : "s"} restante${
                                      daysRemaining === 1 ? "" : "s"
                                    }`
                                  : "Prazo ativo"}
                              </span>
                              <span className="text-[8px] text-slate-600">
                                {formatNumber(progress.percent, 0)}%
                              </span>
                            </div>

                            <div className="h-2 overflow-hidden rounded-full bg-slate-950">
                              <div
                                className="h-full rounded-full bg-emerald-400 transition-all duration-500"
                                style={{ width: `${progress.percent}%` }}
                              />
                            </div>

                            <div className="mt-1.5 flex items-center justify-between text-[8px] text-slate-600">
                              <span>{formatDate(contract.started_at || contract.activated_at)}</span>
                              <span>{formatDate(contract.maturity_at)}</span>
                            </div>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            setExpandedContractId((current) =>
                              Number(current) === Number(contract.id) ? null : Number(contract.id)
                            )
                          }
                          className="mt-3 flex w-full items-center justify-between rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-[9px] font-bold text-slate-400 transition hover:bg-slate-950"
                        >
                          <span>{isExpanded ? "OCULTAR DETALHES" : "VER DETALHES DO CONTRATO"}</span>
                          {isExpanded ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-slate-800 bg-slate-950/45 p-4">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-[9px] sm:grid-cols-3">
                            <div>
                              <span className="block text-slate-600">Plano</span>
                              <b className="text-slate-300">{contract.plan || contract.plan_name || "--"}</b>
                            </div>
                            <div>
                              <span className="block text-slate-600">Prazo contratado</span>
                              <b className="text-slate-300">{contract.duration_days || "--"} dias</b>
                            </div>
                            <div>
                              <span className="block text-slate-600">Custo da proteção</span>
                              <b className="text-violet-300">{formatBrl(contract.fee_amount)}</b>
                            </div>
                            <div>
                              <span className="block text-slate-600">Criado em</span>
                              <b className="text-slate-300">{formatDate(contract.created_at)}</b>
                            </div>
                            <div>
                              <span className="block text-slate-600">Assinado em</span>
                              <b className="text-slate-300">{formatDate(contract.signed_at)}</b>
                            </div>
                            <div>
                              <span className="block text-slate-600">Ativado em</span>
                              <b className="text-slate-300">
                                {formatDate(contract.activated_at || contract.started_at)}
                              </b>
                            </div>
                            <div>
                              <span className="block text-slate-600">Vencimento</span>
                              <b className="text-white">{formatDate(contract.maturity_at)}</b>
                            </div>
                            <div>
                              <span className="block text-slate-600">Hedge</span>
                              <b
                                className={
                                  String(contract.hedge_status || "").toUpperCase() === "CONFIRMED"
                                    ? "text-emerald-300"
                                    : "text-amber-300"
                                }
                              >
                                {contract.hedge_status || "--"}
                              </b>
                            </div>
                            <div>
                              <span className="block text-slate-600">Referência</span>
                              <b className="text-slate-300">
                                {contract.benchmark_symbol || live?.market?.contract || "--"}
                              </b>
                            </div>
                          </div>

                          {contract?.hedge_reference && (
                            <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950 p-3">
                              <span className="block text-[8px] uppercase tracking-wider text-slate-600">
                                Referência do hedge
                              </span>
                              <b className="mt-1 block break-all text-[9px] text-slate-300">
                                {contract.hedge_reference}
                              </b>
                            </div>
                          )}

                          <div className="mt-3 rounded-xl border border-cyan-500/15 bg-cyan-500/5 p-3">
                            <div className="flex gap-2">
                              <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                              <p className="text-[9px] leading-relaxed text-slate-400">
                                O preço exibido é a referência COFY do contrato. O valor de venda do
                                café físico pode ser diferente conforme mercado local, qualidade,
                                logística e condições comerciais.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
